import {
  NavChatActive,
  NavChatInactive,
  NavHomeActive,
  NavHomeInactive,
  NavMoreActive,
  NavMoreInactive,
  NavOperationsActive,
  NavOperationsInactive,
  NavScheduleActive,
  NavScheduleInactive,
  Sparkles,
} from '@/components/icons';
import { useIsAdmin } from '@/hooks/useIsCompanyAdmin';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { useTotalUnreadChatCount } from '@/lib/convex-hooks';
import { useCurrentCrewMember } from '@/hooks/useCurrentCrewMember';
import { hapticLight } from '@/lib/haptics';

export type TabId = 'home' | 'messages' | 'assistant' | 'operations' | 'schedule' | 'more';

interface NavTab {
  id: TabId;
  path: string;
  inactiveIcon: React.ReactNode;
  activeIcon: React.ReactNode;
  label: string;
}

export function BottomNav() {
  const { isAdmin, isLoaded } = useIsAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: currentUser } = useCurrentCrewMember();
  const unreadChatCount = useTotalUnreadChatCount(currentUser?.companyId, currentUser?.id) ?? 0;

  // If admin status is loading ...
  if (!isLoaded) {
    return null
  }

  const allTabs: NavTab[] = [
    { id: 'home', path: '/home', inactiveIcon: <NavHomeInactive className="w-6 h-6" />, activeIcon: <NavHomeActive className="w-6 h-6" />, label: 'Home' },
    { id: 'messages', path: '/conversations', inactiveIcon: <NavChatInactive className="w-6 h-6" />, activeIcon: <NavChatActive className="w-6 h-6" />, label: 'Chat' },
    { id: 'assistant', path: '/assistant', inactiveIcon: <Sparkles className="w-6 h-6" />, activeIcon: <Sparkles className="w-6 h-6" />, label: 'Assistant' },
    { id: 'operations', path: '/operations', inactiveIcon: <NavOperationsInactive className="w-6 h-6" />, activeIcon: <NavOperationsActive className="w-6 h-6" />, label: 'Operations' },
    { id: 'schedule', path: '/schedule', inactiveIcon: <NavScheduleInactive className="w-6 h-6" />, activeIcon: <NavScheduleActive className="w-6 h-6" />, label: 'My Schedule' },
    { id: 'more', path: '/more', inactiveIcon: <NavMoreInactive className="w-6 h-6" />, activeIcon: <NavMoreActive className="w-6 h-6" />, label: 'More' },
  ];

  // Admin sees Home, My Schedule, Chat, Operations, More | Crew sees Home, Chat, My Schedule, More
  // Crew excluded from scheduling (includeInSchedule off) don't get My Schedule.
  const includeInSchedule = currentUser?.includeInSchedule !== false;
  const tabOrder: TabId[] = (isAdmin
    ? ['home', 'schedule', 'messages', 'operations', 'more'] as TabId[]
    : ['home', 'messages', 'schedule', 'more'] as TabId[]
  ).filter(id => includeInSchedule || id !== 'schedule');
  const tabs = tabOrder.map(id => allTabs.find(tab => tab.id === id)!);

  // Determine active tab based on current pathname
  const getActiveTab = (): TabId => {
    const pathname = location.pathname;
    if (pathname.includes('/conversations')) return 'messages';
    if (pathname.includes('/assistant')) return 'assistant';
    if (pathname.includes('/operations')) return 'operations';
    if (pathname.includes('/more')) return 'more';
    if (pathname.includes('/schedule')) return 'schedule';
    return 'home';
  };

  const activeTab = getActiveTab();

  // Don't show bottom nav on sub-screens (specific paths only)
  const shouldShowBottomNav = () => {
    const pathname = location.pathname;
    // Show on main tabs only
    const mainPaths = ['/home', '/messages', '/assistant', '/operations', '/schedule', '/more', '/'];
    if (mainPaths.includes(pathname)) return true;
    // Conversations list/archive yes, individual thread no.
    if (!pathname.startsWith('/conversations')) return false;
    return pathname === '/conversations' || pathname === '/conversations/archive';
  };

  if (!shouldShowBottomNav()) {
    return null;
  }

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-55 pointer-events-auto bg-surface-L0 border-t border-border-L1 shadow-[0px_-4px_6px_-4px_rgba(0,0,0,0.1),0px_-10px_15px_-3px_rgba(0,0,0,0.1)]"
      style={{
        paddingBottom: 'max(8px, calc(var(--safe-bottom)))',
        viewTransitionName: 'bottom-nav',
      }}
    >
      <nav className="app-frame relative flex h-[60px] items-center px-2">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => {
                hapticLight();
                navigate({ to: tab.path });
              }}
              className="flex flex-col items-center flex-1"
            >
              <div className="relative">
                {isActive ? tab.activeIcon : tab.inactiveIcon}
                {tab.id === 'messages' && unreadChatCount > 0 && (
                  <span className="absolute -top-1 -right-2 flex items-center justify-center min-w-4 h-4 px-1 text-[9px] font-semibold text-white bg-content-negative-bold rounded-full">
                    {unreadChatCount > 99 ? '99+' : unreadChatCount}
                  </span>
                )}
              </div>
              <span
                className={`text-[10px] leading-4 font-semibold transition-colors ${
                  isActive ? 'text-content-brand-bold' : 'text-content-primary-subtle'
                }`}
              >
                {tab.label}
              </span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
